import type { Span } from '@sentry/core';

import { mapSentryLevelToELogLevel } from '../internal/integrations/sentry';
import { LoggerCore } from '../internal/LoggerCore';
import { ILoggerConfig } from '../internal/types';
import { DEFAULT_NAMESPACE, ELogLevel } from '../shared/constants';
import {
	ILogContext,
	ILoggerInterface,
	ILogMetadata,
	ILogOptions,
	TLogLevel,
} from '../shared/types';

/**
 * Main Logger class - Interface wrapper around LoggerCore
 * Provides centralized logging with Sentry integration
 */
export class Logger implements ILoggerInterface {
	protected core: LoggerCore;
	protected _namespace?: string;
	static instances: Map<string, Logger> = new Map();

	protected constructor(core: LoggerCore, namespace = DEFAULT_NAMESPACE) {
		this.core = core;
		this._namespace = namespace;
	}

	/**
	 * Initialize logger with configuration
	 * @param config - Logger configuration
	 * @returns Logger instance
	 */
	public static initialize(config: ILoggerConfig): Logger {
		const core = LoggerCore.init(config);
		return new Logger(core);
	}

	/**
	 * Get singleton logger instance
	 * @throws Error if logger not initialized
	 * @returns Logger instance
	 */
	public static getInstance(namespace = DEFAULT_NAMESPACE): Logger {
		let logger = Logger.instances.get(namespace);

		if (!logger) {
			const core = LoggerCore.getInstance();

			logger = new Logger(core, namespace);
			Logger.instances.set(namespace, logger);
		}

		return logger;
	}

	/**
	 * Reset singleton instance (for testing only)
	 * @internal
	 */
	public static resetForTesting(): void {
		LoggerCore.resetInstance();
		Logger.instances.clear();
	}

	/**
	 * Set global context for all loggers
	 * @param context - Context fields to merge
	 */
	public static setGlobalContext(context: Partial<ILogContext>): void {
		LoggerCore.getInstance().setGlobalContext(context);
	}

	/**
	 * Clear global context fields or all global context
	 * @param keys - Optional array of keys to clear
	 */
	public static clearGlobalContext(keys?: string[]): void {
		LoggerCore.getInstance().clearGlobalContext(keys);
	}

	/**
	 * Get global context
	 * @returns Copy of global context
	 */
	public static getGlobalContext(): ILogContext {
		return LoggerCore.getInstance().getGlobalContext();
	}

	/**
	 * Set context for this logger's namespace
	 * @param context - Context fields to merge
	 */
	public setContext(context: Partial<ILogContext>): void {
		this.core.setContext(context, this._namespace);
	}

	/**
	 * Clear context for this logger's namespace
	 * @param keys - Optional array of keys to clear
	 */
	public clearContext(keys?: string[]): void {
		this.core.clearContext(keys, this._namespace);
	}

	/**
	 * Get current context (merged global + namespace)
	 * @returns Merged context where namespace overrides global on conflicts
	 */
	public getContext(): ILogContext {
		return this.core.getMergedContext(this._namespace);
	}

	/**
	 * Log at DEBUG level
	 * @param message - Log message
	 * @param metadata - Optional metadata
	 * @param options - Optional log options
	 */
	public debug(
		message: string,
		metadata?: ILogMetadata,
		options?: ILogOptions
	): void {
		this.core.log(ELogLevel.DEBUG, message, metadata, this._namespace, options);
	}

	/**
	 * Log at INFO level
	 * @param message - Log message
	 * @param metadata - Optional metadata
	 * @param options - Optional log options
	 */
	public info(
		message: string,
		metadata?: ILogMetadata,
		options?: ILogOptions
	): void {
		this.core.log(ELogLevel.INFO, message, metadata, this._namespace, options);
	}

	/**
	 * Log at WARN level
	 * @param message - Log message
	 * @param metadata - Optional metadata
	 * @param options - Optional log options
	 */
	public warn(
		message: string,
		metadata?: ILogMetadata,
		options?: ILogOptions
	): void {
		this.core.log(ELogLevel.WARN, message, metadata, this._namespace, options);
	}

	/**
	 * Log at ERROR level
	 * @param message - Log message
	 * @param error - Optional error object
	 * @param metadata - Optional metadata
	 * @param options - Optional log options
	 */
	public error(
		message: string,
		error?: Error | unknown,
		metadata?: ILogMetadata,
		options?: ILogOptions
	): void {
		const enrichedMetadata = error ? { ...metadata, error } : metadata;
		this.core.log(
			ELogLevel.ERROR,
			message,
			enrichedMetadata,
			this._namespace,
			options
		);
	}

	/**
	 * Performance tracking wrapper
	 * @param name - Operation name
	 * @param operation - Operation type
	 * @param callback - Async callback to execute
	 * @param attributes - Optional attributes
	 * @returns Result of callback
	 */
	public async trackPerformance<T>(
		name: string,
		operation: string,
		callback: (span?: Span) => Promise<T>,
		attributes?: Record<string, any>
	): Promise<T> {
		return this.core.trackPerformance(name, operation, callback, attributes);
	}

	/**
	 * Add breadcrumb (Sentry integration)
	 * @param message - Breadcrumb message
	 * @param category - Optional category
	 * @param level - Optional level
	 * @param data - Optional data
	 */
	public breadcrumb(
		message: string,
		category = this._namespace,
		level?: TLogLevel,
		data?: Record<string, any>
	): void {
		this.core.breadcrumb(
			message,
			category,
			mapSentryLevelToELogLevel(level),
			data,
			this._namespace
		);
	}
}
